import { useCallback, useMemo, useRef } from 'react'
import useSwr, { mutate as mutateGlobal } from 'swr'
import type { AxiosError } from 'axios'
import { events, useSubscribe } from '@fs/zion-tree-pubsub'
import type { BasePerson, FamilyMemberChild, FamilyMemberPerson, PersonFamily, PID } from '@fs/zion-tree-types'
import { useGroupsContext } from '@fs/zion-tree-groups'
import {
  toFamilyOrdinances,
  type FamilyOrdinancesFamily,
  type FamilyPersonOrdinances,
  type FamilyStructure,
} from '@fs/zion-person-ordinance-view'
import useEffectiveUserId from '../hooks/useEffectiveUserId'
import getCoupleIdFromParentIds from '../person-page/components/family-members/getCoupleId'
import { readOrdinances } from './emulators/ordinancesBE'
import { swrKey as ordinancesUrl } from './useOrdinances'
import { fetcher } from './useSwrRequest'
import { getPrimaryParents, primaryParentsUrl, type PrimaryParents } from './usePrimaryParents'

const TREE_DATA = '/service/tree/tree-data'
const UNKNOWN_ID = 'UNKNOWN'
const CALCULATING_REFRESH_MS = 3000
const MAX_CALCULATING_REFRESHES = 20
const SWR_KEY_PREFIX = 'familyOrdinances'

type PeopleById = Partial<Record<PID, FamilyPersonOrdinances>>

/** Everyone but the focus person, whose ordinances come from the Personal tab's data. */
type FamilyOrdinancesData = {
  peopleById: PeopleById
  structures: FamilyStructure[]
}

function hasCalculatingOrdinances(data: FamilyOrdinancesData | undefined): boolean {
  return Object.values(data?.peopleById ?? {}).some((personOrdinances) =>
    personOrdinances?.ordinances.ordinances.some(({ status }) => status === 'Calculating')
  )
}

type FamilyOrdinancesResponse = {
  families: FamilyOrdinancesFamily[]
  error?: AxiosError
  isLoading: boolean
  /** Refetches the given people's ordinances, for the family pieces' onReserve, onUnreserve, onShare, and onPrint. */
  refreshPeople: (personIds: string[]) => void
}

function getParents({ parent1, parent2 }: PersonFamily): FamilyMemberPerson[] {
  return [parent1, parent2].filter((parent): parent is FamilyMemberPerson => !!parent && parent.id !== UNKNOWN_ID)
}

function getCoupleId(family: PersonFamily): string {
  return family.coupleId ?? getCoupleIdFromParentIds(family.parent1?.id, family.parent2?.id)
}

async function fetchChildren(family: PersonFamily, focusPersonId: PID): Promise<FamilyMemberChild[]> {
  if (family.children) {
    return family.children
  }
  if (!family.childrenCount) {
    return []
  }
  const url = `${TREE_DATA}/r9/family-members/couple/${getCoupleId(family)}/children?focusPersonId=${focusPersonId}`
  return (await fetcher<FamilyMemberChild[]>(url, undefined)) ?? []
}

/**
 * Each person's ordinances from the same endpoint as the Personal tab, so the tabs always agree, and each parent's
 * preferred parents for their sealing to parents.
 */
async function fetchPeople(
  people: BasePerson[],
  parentIds: Set<string>,
  owner: string,
  treeId: string
): Promise<PeopleById> {
  const entries = await Promise.all(
    people.map(async (person) => {
      const [ordinances, primaryParents] = await Promise.all([
        readOrdinances(ordinancesUrl(person.id, owner, treeId)),
        parentIds.has(person.id) ? fetcher<PrimaryParents[]>(primaryParentsUrl(person.id), undefined) : undefined,
      ])
      return [person.id as PID, ordinances && { person, ordinances, primaryParents: getPrimaryParents(primaryParents) }]
    })
  )
  return Object.fromEntries(entries.filter(([, personOrdinances]) => personOrdinances))
}

async function fetchFamilyOrdinances(
  personFamilies: PersonFamily[],
  focusPersonId: PID,
  owner: string,
  treeId: string
): Promise<FamilyOrdinancesData> {
  const familyMembers = await Promise.all(
    personFamilies.map(async (family) => ({
      family,
      parents: getParents(family),
      children: (await fetchChildren(family, focusPersonId)).map(({ child }) => child),
    }))
  )
  const people = new Map<string, BasePerson>()
  const parentIds = new Set<string>()
  familyMembers.forEach(({ parents, children }) => {
    parents.forEach((parent) => parentIds.add(parent.id))
    ;[...parents, ...children].forEach((person) => {
      if (person.id !== focusPersonId) {
        people.set(person.id, person)
      }
    })
  })

  return {
    peopleById: await fetchPeople(Array.from(people.values()), parentIds, owner, treeId),
    structures: familyMembers.map(({ family, parents, children }) => ({
      coupleId: getCoupleId(family),
      parentIds: parents.map(({ id }) => id as PID),
      childIds: children.map(({ id }) => id as PID),
    })),
  }
}

/** Refetches only the given people's ordinances. */
async function refreshMembers(
  data: FamilyOrdinancesData,
  personIds: Set<string>,
  owner: string,
  treeId: string
): Promise<FamilyOrdinancesData> {
  const people = Object.values(data.peopleById).flatMap((personOrdinances) =>
    personOrdinances && personIds.has(personOrdinances.person.id) ? [personOrdinances.person] : []
  )
  const refreshed = await fetchPeople(people, new Set(), owner, treeId)
  const peopleById = { ...data.peopleById }
  Object.entries(refreshed).forEach(([id, personOrdinances]) => {
    if (personOrdinances) {
      peopleById[id as PID] = { ...personOrdinances, primaryParents: data.peopleById[id as PID]?.primaryParents }
    }
  })
  return { ...data, peopleById }
}

function includesAnyone(data: FamilyOrdinancesData, personIds: string[]): boolean {
  return personIds.some((id) => data.peopleById[id as PID])
}

/** Refetches the given people in the family tabs, for changes made outside them, like the header's temple indicator. */
export function useRefreshFamilyOrdinancesPeople(): (personIds: string[]) => Promise<unknown> {
  const { effectiveUserId } = useEffectiveUserId()
  const { currentTreeId } = useGroupsContext()
  const treeId = currentTreeId || 'PRIVATE'
  return useCallback(
    (personIds: string[]) => {
      if (!effectiveUserId) {
        return Promise.resolve()
      }
      return mutateGlobal(
        (key) => Array.isArray(key) && key[0] === SWR_KEY_PREFIX && key[1] === effectiveUserId && key[2] === treeId,
        (current?: FamilyOrdinancesData) => {
          if (!current || !includesAnyone(current, personIds)) {
            return current
          }
          return refreshMembers(current, new Set(personIds), effectiveUserId, treeId)
        },
        { revalidate: false }
      )
    },
    [effectiveUserId, treeId]
  )
}

function getFamilyKey({ parent1, parent2, children, childrenCount }: PersonFamily): string {
  const childKey = children ? children.map(({ child }) => child.id).join(',') : childrenCount
  return `${parent1?.id ?? ''}~${parent2?.id ?? ''}~${childKey ?? ''}`
}

/**
 * Loads every family's children and ordinances together, so a family tab renders once with all of its cards and its
 * action bar acts on everyone.
 */
export default function useFamilyOrdinances(
  personFamilies: PersonFamily[],
  focusPersonId: PID | undefined,
  focusPersonOrdinances: FamilyPersonOrdinances | undefined
): FamilyOrdinancesResponse {
  const { effectiveUserId } = useEffectiveUserId()
  const { currentTreeId } = useGroupsContext()
  const treeId = currentTreeId || 'PRIVATE'
  const calculatingRefreshes = useRef(0)

  let key: string[] | null = null
  if (effectiveUserId && focusPersonId && personFamilies.length > 0) {
    key = [SWR_KEY_PREFIX, effectiveUserId, treeId, focusPersonId, ...personFamilies.map(getFamilyKey)]
  }

  const { data, error, isLoading, mutate } = useSwr<FamilyOrdinancesData, AxiosError>(
    key,
    () => fetchFamilyOrdinances(personFamilies, focusPersonId as PID, effectiveUserId as string, treeId),
    {
      keepPreviousData: true,
      onSuccess: (latest) => {
        calculatingRefreshes.current = hasCalculatingOrdinances(latest) ? calculatingRefreshes.current + 1 : 0
      },
      // Kept inline: SWR reschedules when it changes, which picks up a Calculating status that refreshPeople swapped in.
      refreshInterval: (latest) => {
        const keepAsking = hasCalculatingOrdinances(latest) && calculatingRefreshes.current < MAX_CALCULATING_REFRESHES
        return keepAsking ? CALCULATING_REFRESH_MS : 0
      },
    }
  )

  const refreshPeople = useCallback(
    (affectedPersonIds: string[]) => {
      if (!data || !effectiveUserId || !includesAnyone(data, affectedPersonIds)) {
        return
      }
      calculatingRefreshes.current = 0
      mutate(
        (current) => (current ? refreshMembers(current, new Set(affectedPersonIds), effectiveUserId, treeId) : current),
        { revalidate: false }
      ).catch((err) => console.warn(err))
    },
    [data, effectiveUserId, mutate, treeId]
  )

  // Re-adding a parent changes who is in a family without changing the family key.
  const refreshAll = useCallback(() => {
    mutate().catch((err) => console.warn(err))
  }, [mutate])
  useSubscribe({ evt: events.addPerson }, refreshAll)

  const families = useMemo(() => {
    if (!data || !focusPersonId || !focusPersonOrdinances) {
      return []
    }
    const peopleById = { ...data.peopleById, [focusPersonId]: focusPersonOrdinances }
    return data.structures.map((structure) => toFamilyOrdinances(structure, peopleById))
  }, [data, focusPersonId, focusPersonOrdinances])

  // A null key reports not loading, which would flash the empty state.
  const waitingToStart = personFamilies.length > 0 && !data && !error
  const waitingForFocusPerson = personFamilies.length > 0 && !focusPersonOrdinances && !error
  return { families, error, isLoading: isLoading || waitingToStart || waitingForFocusPerson, refreshPeople }
}
